import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Copy, Trash2, ExternalLink } from 'lucide-react';

export function DuplicateReviewDialog({ open, onOpenChange, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.getProjectDuplicates()
      .then(r => setData(r.data))
      .catch(err => toast.error(err.response?.data?.detail || 'Duplikate konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (open) { setConfirmBulk(false); setConfirmId(null); load(); } }, [open, load]);

  const del = async (ids) => {
    setBusy(true);
    try {
      const r = await api.cleanupProjectDuplicates(ids);
      toast.success(`${r.data.deleted} Duplikat${r.data.deleted !== 1 ? 'e' : ''} gelöscht${r.data.skipped?.length ? ` · ${r.data.skipped.length} übersprungen (Regel nicht mehr erfüllt)` : ''}`);
      setConfirmBulk(false); setConfirmId(null);
      load();
      onDeleted?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    setBusy(false);
  };

  const groups = data?.groups || [];
  const recIds = groups.flatMap(g => g.items.filter(i => i.recommended_delete).map(i => i.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2 text-base">
            <Copy className="w-4 h-4 text-amber-400" /> Duplikate im Deal Explorer
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground" data-testid="duplicate-summary">
                {groups.length} Gruppe{groups.length !== 1 ? 'n' : ''} mit gleichem Firmennamen · <span className="text-red-300 font-medium">{recIds.length} zum Löschen empfohlen</span> — die am weitesten fortgeschrittene Version bleibt immer (Badge „bleibt"), alle weiteren Kopien in Sourcing-Stages ohne Briefe werden empfohlen. Ursache: Batch-Importe ohne Duplikat-Prüfung — der Import überspringt bestehende Namen jetzt automatisch.
              </p>
              {recIds.length > 0 && (confirmBulk ? (
                <span className="flex items-center gap-2 ml-auto">
                  <span className="text-[11px] text-amber-300">{recIds.length} Deals endgültig löschen?</span>
                  <Button size="sm" onClick={() => del(recIds)} disabled={busy} data-testid="duplicate-bulk-confirm"
                    className="h-7 text-[11px] rounded-sm bg-red-600 hover:bg-red-500 text-white">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Ja, löschen
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmBulk(false)} data-testid="duplicate-bulk-cancel"
                    className="h-7 text-[11px] rounded-sm border-border text-muted-foreground">Nein</Button>
                </span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setConfirmBulk(true)} data-testid="duplicate-bulk-delete-btn"
                  className="h-7 text-[11px] rounded-sm border-red-500/40 text-red-300 hover:bg-red-500/10 ml-auto">
                  <Trash2 className="w-3 h-3" /> Alle empfohlenen löschen ({recIds.length})
                </Button>
              ))}
            </div>
            {groups.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="duplicate-empty">Keine Duplikate gefunden.</p>
            )}
            <div className="space-y-3">
              {groups.map((g, gi) => (
                <div key={gi} className="border border-border rounded-sm" data-testid={`duplicate-group-${gi}`}>
                  <p className="px-3 py-2 text-xs font-medium text-foreground border-b border-border bg-secondary/20">{g.name}</p>
                  {g.items.map(it => (
                    <div key={it.id} className="px-3 py-2 flex items-center gap-2 flex-wrap border-b border-border last:border-b-0"
                      data-testid={`duplicate-item-${it.id}`}>
                      <button onClick={() => { onOpenChange(false); navigate(`/projects/${it.id}`); }}
                        className="text-xs text-foreground hover:text-purple-300 transition-colors flex items-center gap-1">
                        {it.name} <ExternalLink className="w-3 h-3 text-muted-foreground" />
                      </button>
                      <span className={`text-[9px] font-mono rounded px-1.5 py-0.5 border ${it.recommended_delete
                        ? 'text-amber-300 border-amber-500/40 bg-amber-500/10'
                        : 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'}`}>
                        {it.stage_label}
                      </span>
                      {it.keeper && (
                        <span className="text-[9px] font-mono font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5"
                          title="Am weitesten fortgeschrittene Version — bleibt bestehen"
                          data-testid={`duplicate-keeper-${it.id}`}>BLEIBT</span>
                      )}
                      {it.letters > 0 && <span className="text-[10px] text-muted-foreground">{it.letters} Brief{it.letters !== 1 ? 'e' : ''}</span>}
                      <span className="text-[10px] text-muted-foreground">{it.created_at ? new Date(it.created_at).toLocaleDateString('de-DE') : ''}</span>
                      {it.recommended_delete && (
                        <span className="ml-auto flex items-center gap-1.5">
                          {confirmId === it.id ? (
                            <>
                              <span className="text-[10px] text-amber-300">Endgültig löschen?</span>
                              <button onClick={() => del([it.id])} disabled={busy} data-testid={`duplicate-delete-confirm-${it.id}`}
                                className="text-[10px] font-mono uppercase border border-red-500 bg-red-600/20 text-red-300 hover:bg-red-600/30 rounded px-1.5 py-0.5">Ja</button>
                              <button onClick={() => setConfirmId(null)} data-testid={`duplicate-delete-cancel-${it.id}`}
                                className="text-[10px] font-mono uppercase border border-border text-muted-foreground rounded px-1.5 py-0.5">Nein</button>
                            </>
                          ) : (
                            <button onClick={() => setConfirmId(it.id)} data-testid={`duplicate-delete-${it.id}`}
                              className="text-[10px] font-mono uppercase border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded px-1.5 py-0.5 flex items-center gap-1">
                              <Trash2 className="w-3 h-3" /> Löschen
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
